import is from '@sindresorhus/is';

import type { RecommendedAgent, RecommendedAgentsResponse } from '../data-contract/recommended-agents';
import { recommendedAgentsTranslationKeys as keys } from './recommended-agents-translations';

export type RecommendedAgentsSubject = 'job-level' | 'job-role' | 'position';

type LeverKey =
    | typeof keys.leverAgenticAi
    | typeof keys.leverOtherTool
    | typeof keys.leverPhysicalAutomation
    | typeof keys.leverSoftwareRpa;

const LEVER_KEYS: Readonly<Record<string, LeverKey>> = {
    AGENTIC_AI: keys.leverAgenticAi,
    OTHER_TOOL: keys.leverOtherTool,
    PHYSICAL_AUTOMATION: keys.leverPhysicalAutomation,
    SOFTWARE_RPA: keys.leverSoftwareRpa,
};

export type RecommendedAgentRow = {
    readonly agentId: string;
    readonly autonomyLevel: number | undefined;
    readonly barRatio: number;
    readonly businessFunction: string | undefined;
    readonly description: string | undefined;
    readonly hoursPerWeek: string;
    readonly leverKeys: readonly LeverKey[];
    readonly share: string | undefined;
    readonly tasks: readonly { readonly id: string; readonly title: string; readonly version: number }[];
    readonly title: string;
};

const nonEmpty = (value: string | null | undefined): string | undefined =>
    is.nonEmptyStringAndNotWhitespace(value) ? value.trim() : undefined;

const formatOneDecimal = (value: number, locale: string): string =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 1, minimumFractionDigits: 0 }).format(value);

/** The agents as the list shows them, in the backend's order; bars scale to the agent that frees up the most time. */
export const toRecommendedAgentRows = (
    response: RecommendedAgentsResponse,
    locale: string,
): readonly RecommendedAgentRow[] => {
    const topHours = Math.max(0, ...response.agents.map((agent) => agent.hoursPerWeek));

    return response.agents.map((agent: RecommendedAgent) => ({
        agentId: agent.agentId,
        autonomyLevel: agent.autonomyLevel ?? undefined,
        barRatio: topHours > 0 ? agent.hoursPerWeek / topHours : 0,
        businessFunction: nonEmpty(agent.businessFunction),
        description: nonEmpty(agent.shortDefinition),
        hoursPerWeek: formatOneDecimal(agent.hoursPerWeek, locale),
        leverKeys: agent.automationTypes.flatMap((lever) => {
            const key = LEVER_KEYS[lever];

            return key === undefined ? [] : [key];
        }),
        share: is.number(agent.effortShare) ? formatOneDecimal(agent.effortShare * 100, locale) : undefined,
        tasks: agent.tasks.map((task) => ({ id: task.taskId, title: task.title, version: task.taskVersion })),
        title: nonEmpty(agent.title) ?? agent.agentId,
    }));
};

export const shareKeyOf = (subject: RecommendedAgentsSubject) =>
    subject === 'position' ? keys.sharePosition : subject === 'job-level' ? keys.shareLevel : keys.shareRole;

export const descriptionKeyOf = (subject: RecommendedAgentsSubject) =>
    subject === 'position'
        ? keys.descriptionPosition
        : subject === 'job-level'
          ? keys.descriptionLevel
          : keys.descriptionRole;
